import { withDevtools } from '@angular-architects/ngrx-toolkit';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import {
  ActivityInteractionTimelineEvent,
  ActivityTimeline,
  ConfigurationSetting,
  EntityEvent,
  EntryType,
  InteractionListItem,
  InteractionTimelineEvent,
  ListItem,
  RelatedEntry,
  TimelineConfiguration,
  TimelineEvent,
  User,
  ModuleType,
} from '@maximizer/core/shared/domain';
import { ShowPreviewDialogArgs } from '@maximizer/core/shared/ui/config';
import { Subject } from 'rxjs';
import { computed } from '@angular/core';
import {
  TimelineFilter,
  TimelineMainConfigurationSetting,
  TimelineGridConfigurationSetting,
  TimelineKeys,
} from './timeline.models';
import { startOfDay } from 'date-fns';
import { TimelineShowItemFor } from '../enum/timeline.enums';

export type TimelineViewState = {
  timelineEvents: TimelineEvent[];
  activities: ActivityTimeline[];
  futureActivitiesQuantity: number;
  currentUser: User | undefined;
  users: User[];
  interactionTypes: InteractionListItem[];
  noteAllowedTypes: ListItem<string>[] | undefined;
  selection: ActivityTimeline[] | TimelineEvent[];
  timelineConfiguration: TimelineConfiguration | null;
  searchFilter: TimelineFilter;
  mainConfigurationLoaded: boolean;
  loading: boolean;
  showFilters: boolean;
  showAll: boolean;
  searchPhrase: string;
  hasActivityData: boolean;
  hasTimelineEventData: boolean;
  isUpcomingOverdueViewExpanded: boolean;
  entryKey: string;
  entryTimelineKeys: TimelineKeys | undefined;
  entryType: EntryType;
  moduleType: ModuleType | undefined;
  currentEntity: TimelineEvent | ActivityTimeline | undefined;
  triggerOnUpdateSingleItem: Subject<TimelineEvent | ActivityTimeline>;
  triggerOnRefresh: Subject<void>;
  triggerOnNextPage: Subject<void>;
  openRelatedEntry: Subject<RelatedEntry>;
  openEntity: Subject<EntityEvent>;
  openPreviewDialog: Subject<ShowPreviewDialogArgs>;
  editEntity: Subject<EntityEvent>;
  deleteLegacyAppointment: Subject<
    InteractionTimelineEvent | ActivityInteractionTimelineEvent
  >;
  showDeleteConfirmation: Subject<void>;
  loadedNextPage: Subject<void>;
  gridViewConfiguration: TimelineGridConfigurationSetting;
  mainConfiguration: TimelineMainConfigurationSetting;
};

const initialShowAll = true;
const initialState: TimelineViewState = {
  timelineEvents: [],
  activities: [],
  futureActivitiesQuantity: 0,
  currentUser: undefined,
  users: [],
  interactionTypes: [],
  noteAllowedTypes: [],
  selection: [],
  timelineConfiguration: null,
  searchFilter: {
    users: [],
    interactions: [],
    notes: [],
    categories: [],
    referenceDate: startOfDay(new Date()),
    showAll: initialShowAll,
    searchPhrase: '',
    showAllCategories: true,
    showItemsFor: TimelineShowItemFor.OnlyEntry,
  },
  mainConfigurationLoaded: false,
  loading: false,
  hasActivityData: false,
  hasTimelineEventData: false,
  isUpcomingOverdueViewExpanded: false,
  showFilters: false,
  showAll: initialShowAll,
  searchPhrase: '',
  entryKey: '',
  entryTimelineKeys: undefined,
  entryType: 'addressBook',
  moduleType: undefined,
  currentEntity: undefined,
  triggerOnUpdateSingleItem: new Subject<TimelineEvent | ActivityTimeline>(),
  triggerOnRefresh: new Subject<void>(),
  triggerOnNextPage: new Subject<void>(),
  openRelatedEntry: new Subject<RelatedEntry>(),
  openEntity: new Subject<EntityEvent>(),
  openPreviewDialog: new Subject<ShowPreviewDialogArgs>(),
  editEntity: new Subject<EntityEvent>(),
  deleteLegacyAppointment: new Subject<
    InteractionTimelineEvent | ActivityInteractionTimelineEvent
  >(),
  showDeleteConfirmation: new Subject(),
  loadedNextPage: new Subject(),
  gridViewConfiguration: {
    columnOrder: null,
    columnSize: null,
  },
  mainConfiguration: {
    main: null,
    filters: [],
  },
};

export const TimelineViewStore = signalStore(
  withDevtools('timeline-view'),
  withState(initialState),
  withComputed((state) => ({
    hasActivityData: computed(() => state.activities().length > 0),
    hasTimelineEventData: computed(() => state.timelineEvents().length > 0),
  })),
  withMethods((store) => ({
    reset(): void {
      patchState(store, () => {
        return {
          activities: [],
          timelineEvents: [],
        };
      });
    },
    triggerRefreshEvent(force?: boolean): void {
      if (!store.loading() || force) {
        store.triggerOnRefresh().next();
      }
    },
    setActivities(activities: ActivityTimeline[]): void {
      patchState(store, () => ({
        activities,
      }));
    },
    setTimelineEvents(timelineEvents: TimelineEvent[]): void {
      patchState(store, () => ({
        timelineEvents,
      }));
    },
    setFutureActivitiesQuantity(futureActivitiesQuantity: number): void {
      patchState(store, () => ({
        futureActivitiesQuantity,
      }));
    },
    deleteActivities(keys: string[]): void {
      const newActivities = store
        .activities()
        .map((item) => item)
        .filter((item) => !keys.find((key) => item.key === key));
      this.setActivities(newActivities);
    },
    deleteTimelineEvents(keys: string[]): void {
      const newTimeline = store
        .timelineEvents()
        .map((item) => item)
        .filter((item) => !keys.find((key) => item.key === key));
      this.setTimelineEvents(newTimeline);
    },
    updateSelection(selection: ActivityTimeline[] | TimelineEvent[]): void {
      patchState(store, () => ({ selection }));
    },
    updateSingleItem(updatedEvent: TimelineEvent | ActivityTimeline): void {
      let result: TimelineEvent | ActivityTimeline | undefined;
      const activityEvent = store
        .activities()
        .find((activity) => activity.key === updatedEvent.key);
      result = activityEvent && Object.assign(activityEvent, updatedEvent);

      const timelineEvent = store
        .timelineEvents()
        .find((event) => event.key === updatedEvent.key);
      result =
        (timelineEvent && Object.assign(timelineEvent, updatedEvent)) ?? result;

      if (result) {
        store.triggerOnUpdateSingleItem().next(result);
      }
    },
    setCurrentUser(currentUser: User): void {
      patchState(store, () => ({
        currentUser,
      }));
    },
    setUsers(users: User[]): void {
      patchState(store, () => ({
        users,
      }));
    },
    setTimelineConfiguration(
      timelineConfiguration: TimelineConfiguration,
    ): void {
      patchState(store, () => ({
        timelineConfiguration,
      }));
    },
    setTimelineFilter(searchFilter: TimelineFilter): void {
      patchState(store, () => ({
        searchFilter,
      }));
    },
    setInteractionTypes(interactionTypes: InteractionListItem[]): void {
      patchState(store, () => ({
        interactionTypes,
      }));
    },
    setNoteAllowedTypes(
      noteAllowedTypes: ListItem<string>[] | undefined,
    ): void {
      patchState(store, () => ({
        noteAllowedTypes,
      }));
    },
    setEntryKey(entryKey: string): void {
      patchState(store, () => ({
        entryKey,
      }));
    },
    setModuleType(moduleType: ModuleType): void {
      patchState(store, () => ({
        moduleType,
      }));
    },
    setEntryTimelineKeys(entryTimelineKeys?: TimelineKeys): void {
      patchState(store, () => ({
        entryTimelineKeys,
      }));
    },
    setEntryType(entryType: EntryType): void {
      patchState(store, () => ({
        entryType,
      }));
    },
    setCurrentEntity(currentEntity: TimelineEvent | ActivityTimeline): void {
      patchState(store, () => ({
        currentEntity,
      }));
    },
    setSearchPhrase(searchPhrase: string): void {
      patchState(store, () => ({
        searchPhrase,
      }));
    },
    setShowFilters(showFilters: boolean): void {
      patchState(store, () => ({
        showFilters,
      }));
    },
    setShowAll(showAll: boolean): void {
      patchState(store, () => ({
        showAll,
        timelineEvents: [],
      }));
    },
    setMainConfigurationLoaded(mainConfigurationLoaded: boolean): void {
      patchState(store, () => ({
        mainConfigurationLoaded,
      }));
    },
    setMainConfiguration(main: ConfigurationSetting | null): void {
      patchState(store, (state) => ({
        mainConfiguration: { ...state.mainConfiguration, main },
      }));
    },
    setGridViewColumnOrder(columnOrder: ConfigurationSetting | null): void {
      patchState(store, (state) => ({
        gridViewConfiguration: { ...state.gridViewConfiguration, columnOrder },
      }));
    },
    setGridViewColumnSize(columnSize: ConfigurationSetting | null): void {
      patchState(store, (state) => ({
        gridViewConfiguration: { ...state.gridViewConfiguration, columnSize },
      }));
    },
    toggleLoading(loading: boolean): void {
      patchState(store, () => ({
        loading,
      }));
    },
    setIsUpcomingOverdueViewExpanded(
      isUpcomingOverdueViewExpanded: boolean,
    ): void {
      patchState(store, () => ({
        isUpcomingOverdueViewExpanded,
      }));
    },
    reinitialize(): void {
      patchState(store, () => ({
        ...initialState,
        triggerOnUpdateSingleItem: new Subject<
          TimelineEvent | ActivityTimeline
        >(),
        triggerOnRefresh: new Subject<void>(),
        triggerOnNextPage: new Subject<void>(),
        openRelatedEntry: new Subject<RelatedEntry>(),
        openEntity: new Subject<EntityEvent>(),
        openPreviewDialog: new Subject<ShowPreviewDialogArgs>(),
        editEntity: new Subject<EntityEvent>(),
        loadedNextPage: new Subject<void>(),
        deleteLegacyAppointment: new Subject<
          InteractionTimelineEvent | ActivityInteractionTimelineEvent
        >(),
        showDeleteConfirmation: new Subject<void>(),
      }));
    },
  })),
);

export type TimelineViewStore = InstanceType<typeof TimelineViewStore>;
